import {isPersonalOrganization, isTeamOrganization} from '../common/organizationKind.js';
import {AuthSnapshot} from './auth.js';
import {api} from './api.js';
import {apiUrl} from './config.js';
import {escapeHtml, renderLayout} from './layout.js';
import QRCode from 'qrcode';
import {LEGAL_PLUS_PRODUCT_ID,LEGAL_PLUS_PRICE_ID} from '../common/legalReviewTier.js';

/**
 * 专项服务产品定义契约
 */
type ServicePlan = {
  id: string;
  priceId: string;
  name: string;
  price: number;
  interval: 'day' | 'month' | 'year' | 'once';
  categoryKey: 'finance' | 'legal' | 'general';
  enabled: boolean;
  features: string[];
  description?: string;
  highlight?: string;
  sortOrder?: number;
  tier?: 'base'|'plus';
};

type BillingCatalogPayload = {
  products: Array<{
    id: string;
    enabled: boolean;
    name?: string;
    moduleId?: string;
    description?: string;
    features?: string[];
    highlight?: string;
    sortOrder?: number;
  }>;
  prices: Array<{
    id: string;
    productId: string;
    amount: number;
    interval: 'day'|'month'|'year'|'once';
    active: boolean;
    version: number;
  }>;
};

/**
 * 订单条目定义契约
 */
type OrderRecord = {
  id: string;
  orderNo: string;
  status: 'pending' | 'paid' | 'fulfilled' | 'cancelled' | 'refunded';
  amount: number;
  seatQuantity: number;
  ownerType: 'personal' | 'enterprise';
  productSnapshot?: {
    name?: string;
    id?: string;
  };
  paidAt: string | null;
  hiddenAt: string | null;
  createdAt?: string;
};

/**
 * 订阅授权条目定义契约
 */
type SubscriptionRecord = {
  productId: string;
  status: string;
  seatQuantity?: number;
  ownerType?: string;
  expiresAt?: string | null;
};

type ModuleSeatRecord = {
  id: string;
  productId: string;
  userId: string;
  status: string;
  endsAt: string | null;
  assignedAt: string;
};

type OrganizationMemberRecord = {
  id: string;
  email: string;
  displayName: string;
};

/**
 * 专项服务目录清单
 */
const defaultPlans: ServicePlan[] = [
  {
    id: 'billing-product-finance',
    priceId: 'billing-price-finance-monthly',
    name: '财务管理模块',
    price: 199,
    interval: 'month',
    categoryKey: 'finance',
    enabled: true,
    features: ['报关单及发票智能匹配核对', '发票台账全自动生成与退税申报', '多源资料一键归档与协同共享'],
  },
  {
    id: 'billing-product-legal',
    priceId: 'billing-price-legal-monthly',
    name: '法务合规助手',
    price: 299,
    interval: 'month',
    categoryKey: 'legal',
    enabled: false,
    features: ['智能初审与合同风险排查', '法律法规及司法裁判全库检索', '在线批注版与审核意见书协同导出'],
  },
];
const legalPlusPlan: ServicePlan = {
  id: LEGAL_PLUS_PRODUCT_ID,
  priceId: LEGAL_PLUS_PRICE_ID,
  name: '法务合规助手 Plus',
  price: 599,
  interval: 'month',
  categoryKey: 'legal',
  enabled: true,
  tier: 'plus',
  features: ['深度审查与系列专项规则', '企业性质与关联关系识别', '批量任务与增强审核设置'],
};

const BILLING_CATALOG_CACHE_KEY = 'tax-assistant-billing-catalog-v2';

/**
 * 读取上一次成功取得的后台服务目录，作为首屏离线/慢接口时的即时展示值。
 * 目录仍以服务端返回为准，这个缓存只负责避免首屏出现占位价或旧的硬编码价。
 */
function readBillingCatalogCache(basePlans: ServicePlan[]): ServicePlan[] {
  if (typeof window === 'undefined') return basePlans;
  try {
    const raw = window.localStorage.getItem(BILLING_CATALOG_CACHE_KEY);
    if (!raw) return basePlans;
    const cached = JSON.parse(raw) as ServicePlan[];
    if (!Array.isArray(cached)) return basePlans;
    const valid = cached.filter(plan => plan && typeof plan.id === 'string' && typeof plan.price === 'number' && Number.isFinite(plan.price));
    return valid.length ? valid : basePlans;
  } catch {
    return basePlans;
  }
}

function writeBillingCatalogCache(plans: ServicePlan[]): void {
  if (typeof window === 'undefined') return;
  try {
    const payload = plans.map(plan => ({
      id: plan.id,
      priceId: plan.priceId,
      name: plan.name,
      price: plan.price,
      interval: plan.interval,
      categoryKey: plan.categoryKey,
      enabled: plan.enabled,
      features: plan.features,
      description: plan.description ?? '',
      highlight: plan.highlight ?? '',
      sortOrder: plan.sortOrder ?? 100,
      tier: plan.tier,
    }));
    window.localStorage.setItem(BILLING_CATALOG_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* 浏览器禁用 localStorage 时不影响订单中心使用 */
  }
}

/** 后台维护的模块标识决定客户端卡片分类；未在内置分类中的服务归入通用卡片。 */
function resolveCategoryKey(moduleId: string, productId: string): ServicePlan['categoryKey'] {
  const value = String(moduleId || '').toLowerCase();
  if (productId === LEGAL_PLUS_PRODUCT_ID || value.includes('legal')) return 'legal';
  if (value.includes('finance')) return 'finance';
  return 'general';
}

/**
 * 以后台服务目录为准生成客户端展示列表。
 * 后台新增的服务直接出现在列表里，停售或删除的服务随即消失；
 * 内置定义只作为价目缺失时的兜底展示信息（分类、默认卖点）。
 */
function mergeBillingCatalogPlans(basePlans: ServicePlan[], payload: BillingCatalogPayload): ServicePlan[] {
  const baseById = new Map(basePlans.map(plan => [plan.id, plan]));
  const priceByProduct = new Map<string, BillingCatalogPayload['prices'][number]>();
  for (const price of payload.prices) {
    if (!price.active || !Number.isFinite(price.amount)) continue;
    const current = priceByProduct.get(price.productId);
    if (!current || price.version > current.version) priceByProduct.set(price.productId, price);
  }
  return [...payload.products]
    .sort((a, b) => Number(a.sortOrder ?? 100) - Number(b.sortOrder ?? 100) || String(a.name ?? '').localeCompare(String(b.name ?? ''), 'zh-CN'))
    .map(product => {
      const base = baseById.get(product.id);
      const price = priceByProduct.get(product.id);
      const features = Array.isArray(product.features) ? product.features.filter(Boolean) : [];
      const fallback: ServicePlan = {
        id: product.id,
        priceId: '',
        name: product.name || '专项服务',
        price: 0,
        interval: 'month',
        categoryKey: resolveCategoryKey(product.moduleId ?? '', product.id),
        enabled: true,
        features: [],
      };
      const source = base ?? fallback;
      return {
        ...source,
        name: product.name || source.name,
        priceId: price?.id ?? source.priceId,
        price: price?.amount ?? source.price,
        interval: price?.interval ?? source.interval,
        enabled: Boolean(product.enabled),
        features: features.length ? features : source.features.length ? source.features : (product.description ? [product.description] : []),
        description: product.description ?? source.description ?? '',
        highlight: product.highlight ?? source.highlight ?? '',
        sortOrder: Number(product.sortOrder ?? source.sortOrder ?? 100),
        categoryKey: base?.categoryKey ?? resolveCategoryKey(product.moduleId ?? '', product.id),
        tier: base?.tier ?? (product.id === LEGAL_PLUS_PRODUCT_ID ? 'plus' : undefined),
      };
    });
}

async function loadInitialBillingCatalog(basePlans: ServicePlan[]): Promise<ServicePlan[]> {
  const cachedPlans = readBillingCatalogCache(basePlans);
  try {
    const payload = await Promise.race([
      api.get<BillingCatalogPayload>(apiUrl('/api/billing/products')),
      new Promise<BillingCatalogPayload>((_, reject) => window.setTimeout(() => reject(new Error('billing catalog timeout')), 2500)),
    ]);
    // 后台目录是唯一事实来源：为空表示管理员已下架全部服务，同样需要如实呈现。
    const plans = mergeBillingCatalogPlans(basePlans, payload);
    writeBillingCatalogCache(plans);
    return plans;
  } catch {
    return cachedPlans;
  }
}

type EntitlementModuleRecord = {
  moduleId: string;
  productId: string;
  productName: string;
  available: boolean;
  grantType: 'admin_granted' | 'purchased' | 'seat_assigned' | 'trial' | null;
  scopeType: 'personal' | 'enterprise' | null;
  status: string;
  expiresAt: string | null;
};

type EntitlementsPayload = {
  personal: {hasAnyEntitlement: boolean; modules: EntitlementModuleRecord[]};
  enterprise: {hasAnyEntitlement: boolean; modules: EntitlementModuleRecord[]} | null;
  alternateScopeHint: {
    personalHasEntitlement: boolean;
    enterpriseHasEntitlement: boolean;
    suggestedScope: 'personal' | 'enterprise' | null;
  };
};

type BillingOrganization = {
  id: string;
  name: string;
  organizationCode?: string;
  ownerUserId: string;
  role?: string;
};

/**
 * 缓存当前页面状态与数据列表
 */
let activeAuthSnapshot: AuthSnapshot | null = null;
let cachedPlans: ServicePlan[] = readBillingCatalogCache([...defaultPlans, legalPlusPlan]);
let cachedOrders: OrderRecord[] = [];
let cachedSubscriptions: SubscriptionRecord[] = [];
let cachedEntitlements: EntitlementsPayload | null = null;
let currentFilter: 'all' | 'fulfilled' | 'pending' | 'cancelled' = 'all';
let selectedOrderIds = new Set<string>();

/** 当前部署环境标识，用于隔离订单中心 Tab 记忆。 */
function billingDeploymentEnvironment(): string {
  const host = window.location.hostname.toLowerCase();
  return host.includes('test') || host.includes('localhost') || host.includes('127.0.0.1') ? 'test' : 'production';
}

/** 订单中心 Tab 的 localStorage 隔离键。 */
function billingModeStorageKey(auth: AuthSnapshot): string {
  return `billing-mode:${billingDeploymentEnvironment()}:${auth.user.id}:${auth.organization.id}`;
}

/**
 * 解析订单中心默认视角：URL > localStorage > 智能默认。
 * @param auth 当前登录态
 * @param entitlements 服务端统一授权结果
 */
function resolveBillingMode(auth: AuthSnapshot, entitlements: EntitlementsPayload | null): 'personal' | 'enterprise' {
  const canEnterprise = auth.organization.organizationType === 'team' || Boolean(entitlements?.alternateScopeHint?.enterpriseHasEntitlement);
  const params = new URLSearchParams(window.location.search);
  const scopeParam = params.get('scope');
  if (scopeParam === 'enterprise' && canEnterprise) return 'enterprise';
  if (scopeParam === 'personal') return 'personal';
  const stored = localStorage.getItem(billingModeStorageKey(auth));
  if (stored === 'enterprise' && canEnterprise) return 'enterprise';
  if (stored === 'personal') return 'personal';
  if (entitlements?.alternateScopeHint.suggestedScope === 'enterprise' && canEnterprise) return 'enterprise';
  return 'personal';
}

/** 授权来源对应的前端展示文案。 */
function grantTypeLabel(grantType: EntitlementModuleRecord['grantType']): string {
  if (grantType === 'admin_granted') return '管理员代开通';
  if (grantType === 'purchased') return '自购订阅';
  if (grantType === 'seat_assigned') return '团队席位';
  if (grantType === 'trial') return '新用户试用';
  return '未开通';
}

/**
 * 格式化时间为中文字符串
 * @param dateStr 时间字符串
 * @returns 格式化后的时间文本
 */
function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).replace(/\//g, '-');
  } catch {
    return '—';
  }
}

/**
 * 格式化日期（不含时间），用于有效期展示。
 * @param dateStr 时间字符串
 */
function formatDateOnly(dateStr?: string | null): string {
  if (!dateStr) return '永久有效';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).replace(/\//g, '-');
  } catch {
    return '—';
  }
}

/** 统计企业视角下某产品的有效已购席位数。 */
function sumEnterprisePurchasedSeats(subscriptions: SubscriptionRecord[], productId: string): number {
  const now = Date.now();
  return subscriptions
    .filter(item =>
      item.productId === productId
      && item.ownerType === 'enterprise'
      && item.status === 'active'
      && (!item.expiresAt || Date.parse(item.expiresAt) > now),
    )
    .reduce((sum, item) => sum + (Number(item.seatQuantity) || 1), 0);
}

/** 判断席位授权是否已到期。 */
function isSeatExpired(seat: ModuleSeatRecord): boolean {
  if (seat.status !== 'active') return true;
  if (!seat.endsAt) return false;
  return Date.parse(seat.endsAt) <= Date.now();
}

/**
 * 生成专项服务卡片价格行旁的摘要文案。
 */
function renderModuleStatText(
  mode: 'personal' | 'enterprise',
  entitlement: EntitlementModuleRecord | null,
  purchasedSeats: number,
  plusPlan?: ServicePlan,
  plusEntitlement: EntitlementModuleRecord | null = null,
): string {
  if (!entitlement?.available) return '';

  if (mode === 'personal') return '';

  if (purchasedSeats > 0) return `已购 ${purchasedSeats} 席`;
  return '';
}

/**
 * 卡片标题行与状态徽章之间的有效期文案（已开通时展示）。
 * @param entitlement 当前视角下的模块授权
 */
function renderModuleValidityHeaderText(entitlement: EntitlementModuleRecord | null): string {
  if (!entitlement?.available) return '';
  if (entitlement.expiresAt) return `有效期至 ${formatDateOnly(entitlement.expiresAt)}`;
  return '永久有效';
}

/** 解析卡片右上角状态徽章文案；团队管理员代开通时不展示。 */
function resolveModuleBadgeText(
  mode: 'personal' | 'enterprise',
  entitlement: EntitlementModuleRecord | null,
): string {
  if (!entitlement?.available) return '未开通';
  if (mode === 'enterprise' && entitlement.grantType === 'admin_granted') return '管理员授权';
  return grantTypeLabel(entitlement.grantType);
}

/** 更新卡片价格行旁的摘要节点。 */
function updateModuleStatElement(card: HTMLElement, planId: string, statText: string): void {
  const priceRow = card.querySelector<HTMLElement>('.billing-module-price-row');
  const statEl = card.querySelector<HTMLElement>(`[data-module-stat="${planId}"]`);
  card.querySelector('.billing-module-title-box .billing-module-stat')?.remove();

  if (statText) {
    if (statEl) {
      statEl.textContent = statText;
      statEl.hidden = false;
      return;
    }
    priceRow?.insertAdjacentHTML(
      'beforeend',
      `<span class="billing-module-stat" data-module-stat="${escapeHtml(planId)}">${escapeHtml(statText)}</span>`,
    );
    return;
  }

  statEl?.remove();
}

/** 更新卡片标题行旁的有效期展示。 */
function updateModuleValidityElement(
  card: HTMLElement,
  planId: string,
  entitlement: EntitlementModuleRecord | null,
): void {
  const validityText = renderModuleValidityHeaderText(entitlement);
  let validityEl = card.querySelector<HTMLElement>(`[data-module-validity="${planId}"]`);
  if (!validityText) {
    validityEl?.remove();
    return;
  }
  if (!validityEl) {
    const badge = card.querySelector(`[data-module-badge="${planId}"]`);
    const tierIndicator = planId === LEGAL_PLUS_PRODUCT_ID ? 'plus' : 'base';
    const markup = `<span class="billing-module-validity" data-module-validity="${escapeHtml(planId)}" data-tier-indicator="${tierIndicator}">${escapeHtml(validityText)}</span>`;
    if (badge) badge.insertAdjacentHTML('beforebegin', markup);
    else card.querySelector('.billing-module-top')?.insertAdjacentHTML('beforeend', markup);
    return;
  }
  validityEl.textContent = validityText;
  validityEl.hidden = false;
}

/** 更新卡片右上角状态徽章。 */
function updateModuleBadgeElement(
  card: HTMLElement,
  planId: string,
  mode: 'personal' | 'enterprise',
  entitlement: EntitlementModuleRecord | null,
): void {
  const badge = card.querySelector<HTMLElement>(`[data-module-badge="${planId}"]`);
  if (!badge) return;

  const badgeText = resolveModuleBadgeText(mode, entitlement);
  const hasActiveSub = Boolean(entitlement?.available);
  if (!badgeText) {
    badge.hidden = true;
    return;
  }

  badge.hidden = false;
  badge.className = `billing-badge ${hasActiveSub ? 'is-active' : 'is-inactive'}`;
  badge.textContent = badgeText;
}

/**
 * 格式化金额为两位小数
 * @param amount 金额数值
 * @returns 格式化字符串
 */
function formatCurrency(amount: number): string {
  const value = Number(amount || 0);
  return `¥${value.toLocaleString('zh-CN', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * 渲染 Google Material 3 现代化订单中心主页面
 * @param root 页面容器 DOM 元素
 * @param auth 当前认证用户信息快照
 */
export async function renderBillingPage(root: HTMLElement, auth: AuthSnapshot): Promise<void> {
  activeAuthSnapshot = auth;
  const [organizationResult, entitlementResult, catalogResult] = await Promise.allSettled([
    api.get<{organizations: BillingOrganization[]}>(apiUrl('/api/organizations')),
    api.get<EntitlementsPayload>(apiUrl('/api/billing/entitlements')),
    loadInitialBillingCatalog([...defaultPlans, legalPlusPlan]),
  ]);
  // 首屏直接使用后台价目（或上一次成功价目缓存），不再先渲染“价格加载中”。
  cachedPlans = catalogResult.status === 'fulfilled'
    ? catalogResult.value
    : readBillingCatalogCache([...defaultPlans, legalPlusPlan]);
  const organizations = organizationResult.status === 'fulfilled' ? organizationResult.value.organizations ?? [] : [];
  cachedEntitlements = entitlementResult.status === 'fulfilled' ? entitlementResult.value : null;
  const paymentAccount = auth.user.displayName || auth.user.email;
  const enterpriseOrganizations = organizations.filter(item => isTeamOrganization(item));
  const personalOrganization = organizations.find(item => isPersonalOrganization(item));
  const canEnterprise = enterpriseOrganizations.length > 0;
  const mode = resolveBillingMode(auth, cachedEntitlements);
  localStorage.setItem(billingModeStorageKey(auth), mode);
  const scopeEntitlements = mode === 'enterprise' ? cachedEntitlements?.enterprise : cachedEntitlements?.personal;
  const alternateHint = cachedEntitlements?.alternateScopeHint;
  const showAlternateHint = Boolean(
    alternateHint &&
    ((mode === 'personal' && alternateHint.enterpriseHasEntitlement && !alternateHint.personalHasEntitlement) ||
      (mode === 'enterprise' && alternateHint.personalHasEntitlement && !alternateHint.enterpriseHasEntitlement)),
  );
  const enterprisePurchase = mode === 'enterprise' && canEnterprise && (auth.organization.ownerUserId === auth.user.id || auth.role === 'enterprise_admin');
  currentFilter = 'all';
  // 服务卡片集合完全来自后台目录：新增即出现，停售或删除即消失。
  const initialPlansById = new Map(cachedPlans.map(plan => [plan.id, plan]));
  const initialLegalPlusPlan = initialPlansById.get(legalPlusPlan.id);
  const initialPlans = cachedPlans.filter(plan => plan.id !== legalPlusPlan.id);

  const html = `
  <div class="billing-page-container">
    <!-- 页头导航与主体切换 -->
    <header class="billing-header">
      <div class="billing-header-main">
        <div class="billing-heading-row">
          <h1 class="billing-title">订单中心</h1>
          ${canEnterprise ? `
          <div class="billing-mode-segmented" role="tablist" aria-label="订单主体切换">
        <button type="button" class="billing-segmented-btn ${mode === 'personal' ? 'is-active' : ''}" data-billing-mode="personal">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
            <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
          </svg>
          个人订单
        </button>
        <button type="button" class="billing-segmented-btn ${mode === 'enterprise' ? 'is-active is-enterprise' : ''}" data-billing-mode="enterprise">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
            <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clip-rule="evenodd"/>
          </svg>
          团队订单
        </button>
          </div>
          ` : ''}
        </div>
        <p class="billing-subtitle">管理与订购${mode === 'enterprise' ? '团队' : '个人'}所需的智能办公服务</p>
      </div>
    </header>

    ${showAlternateHint ? `
    <div class="billing-scope-hint" role="status">
      <span>${mode === 'personal' ? '检测到授权记录在团队订单空间，可切换查看团队授权详情。' : '检测到授权记录在个人订单空间，可切换查看个人授权详情。'}</span>
      <button type="button" class="billing-btn billing-btn-secondary" data-billing-mode="${mode === 'personal' ? 'enterprise' : 'personal'}">
        切换到${mode === 'personal' ? '团队' : '个人'}订单
      </button>
    </div>` : ''}

    <!-- 专项服务与订购区 -->
    <section class="billing-services-section" aria-label="专项服务订阅">
      <div class="billing-modules-grid" id="billingModulesGrid">
        ${servicesGridHtml(initialPlans, initialLegalPlusPlan, mode, enterprisePurchase, scopeEntitlements?.modules ?? [])}
      </div>
    </section>

    <!-- 订单历史数据表 -->
    <section class="billing-orders-panel" aria-label="订单交易明细">
      <div class="billing-orders-toolbar">
        <h2 class="billing-orders-title">订单记录</h2>
        <div class="billing-filter-tabs" role="tablist">
          <button type="button" class="billing-filter-tab is-active" data-filter="all">全部订单 <span id="countAll">0</span></button>
          <button type="button" class="billing-filter-tab" data-filter="fulfilled">已支付 <span id="countFulfilled">0</span></button>
          <button type="button" class="billing-filter-tab" data-filter="pending">待支付 <span id="countPending">0</span></button>
          <button type="button" class="billing-filter-tab" data-filter="cancelled">已取消 <span id="countCancelled">0</span></button>
        </div>
        <div class="billing-orders-toolbar-actions">
          <button type="button" class="billing-orders-bulk-delete" id="billingBulkDeleteOrders" hidden disabled>删除所选</button>
          <div class="billing-orders-count" id="billingOrdersSummary">共 0 笔订单</div>
        </div>
      </div>

      <div class="billing-table-responsive">
        <table class="billing-table">
          <thead>
            <tr>
              <th class="billing-order-select-col" id="billingOrderSelectHead" hidden>
                <input type="checkbox" id="billingSelectAllOrders" aria-label="全选当前列表">
              </th>
              <th>下单时间</th>
              <th>产品名称</th>
              <th>版本</th>
              <th>席位数量</th>
              <th>支付金额</th>
              <th>订单状态</th>
              <th style="text-align:right">操作</th>
            </tr>
          </thead>
          <tbody id="billingOrdersTbody">
            <tr><td colspan="8" class="billing-empty-state"><div class="billing-empty-icon" aria-hidden="true">—</div><h3>暂无订单记录</h3><p>开通服务或购买席位后，订单会显示在这里。</p></td></tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
  `;

  renderLayout(root, auth, html);

  // 购买主体切换绑定
  root.querySelectorAll<HTMLButtonElement>('[data-billing-mode]').forEach(button => {
    button.addEventListener('click', async () => {
      const targetMode = button.dataset.billingMode || 'personal';
      const targetOrganization = targetMode === 'enterprise' ? enterpriseOrganizations[0] : personalOrganization;
      if (targetOrganization && targetOrganization.id !== auth.organization.id) {
        button.disabled = true;
        try {
          await api.post(apiUrl(`/api/organizations/enterprise-spaces/${encodeURIComponent(targetOrganization.id)}/select`), {});
        } catch (error) {
          button.disabled = false;
          window.alert(error instanceof Error ? error.message : String(error));
          return;
        }
      }
      localStorage.setItem(billingModeStorageKey(auth), targetMode);
      const url = new URL(window.location.href);
      url.searchParams.set('scope', targetMode);
      window.location.assign(url.toString());
    });
  });

  // 状态筛选 Tab 绑定
  root.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach(tab => {
    tab.addEventListener('click', () => {
      root.querySelectorAll('.billing-filter-tab').forEach(el => el.classList.remove('is-active'));
      tab.classList.add('is-active');
      currentFilter = (tab.dataset.filter as typeof currentFilter) || 'all';
      renderOrdersTable(root, cachedOrders, mode, auth, mode === 'personal' || enterprisePurchase);
    });
  });

  bindServiceCardEvents(root, auth, mode, enterprisePurchase);

  root.querySelector<HTMLInputElement>('#billingSelectAllOrders')?.addEventListener('change', (event) => {
    const checked = (event.currentTarget as HTMLInputElement).checked;
    root.querySelectorAll<HTMLInputElement>('.billing-order-select').forEach(input => {
      input.checked = checked;
      if (checked) selectedOrderIds.add(input.value);
      else selectedOrderIds.delete(input.value);
    });
    refreshOrderSelectionUi(root, mode === 'personal' || enterprisePurchase);
  });
  root.querySelector<HTMLButtonElement>('#billingBulkDeleteOrders')?.addEventListener('click', async () => {
    const canDeleteOrders = mode === 'personal' || enterprisePurchase;
    await deleteSelectedOrders(root, mode, canDeleteOrders);
  });

  // 订单列表操作事件委托：包括【继续支付】和【删除隐藏记录】
  const ordersTbody = root.querySelector<HTMLTableSectionElement>('#billingOrdersTbody');
  ordersTbody?.addEventListener('change', (event) => {
    const input = (event.target as HTMLElement).closest<HTMLInputElement>('.billing-order-select');
    if (!input) return;
    if (input.checked) selectedOrderIds.add(input.value);
    else selectedOrderIds.delete(input.value);
    refreshOrderSelectionUi(root, mode === 'personal' || enterprisePurchase);
  });
  ordersTbody?.addEventListener('click', async (event) => {
    const target = event.target as HTMLElement;

    // 复制订单号
    const copyBtn = target.closest<HTMLButtonElement>('[data-copy-orderno]');
    if (copyBtn) {
      const orderNo = copyBtn.dataset.copyOrderno || '';
      try {
        await navigator.clipboard.writeText(orderNo);
        const originalTitle = copyBtn.title;
        copyBtn.title = '已复制!';
        copyBtn.style.color = '#059669';
        window.setTimeout(() => {
          copyBtn.title = originalTitle;
          copyBtn.style.color = '';
        }, 1500);
      } catch {
        window.prompt('请手动复制订单号：', orderNo);
      }
      return;
    }

    // 待支付订单直接唤起支付
    const payBtn = target.closest<HTMLButtonElement>('[data-order-pay]');
    if (payBtn) {
      const orderId = payBtn.dataset.orderPay || '';
      const order = cachedOrders.find(item => item.id === orderId);
      if (!order) return;
      try {
        payBtn.disabled = true;
        payBtn.textContent = '生成中…';
        const payment = await api.post<{codeUrl: string}>(apiUrl(`/api/billing/orders/${encodeURIComponent(orderId)}/wechat/native-payment`));
        const plan = cachedPlans.find(p => p.id === order.productSnapshot?.id) || cachedPlans[0]!;
        showPaymentModal(root, plan, paymentAccount, order.id, payment.codeUrl, order.seatQuantity || 1, order.ownerType === 'enterprise', order.amount);
      } catch (error) {
        window.alert(error instanceof Error ? error.message : String(error));
      } finally {
        payBtn.disabled = false;
        payBtn.textContent = '立即支付';
      }
      return;
    }

    // 删除订单记录
    const delBtn = target.closest<HTMLButtonElement>('[data-order-delete]');
    if (delBtn) {
      const orderId = delBtn.dataset.orderDelete || '';
      if (!window.confirm('确定删除此条订单记录吗？支付、授权和审计数据将保留，仅不再显示在列表中。')) return;
      try {
        delBtn.disabled = true;
        const result = await api.delete<{ok: boolean; deleted: number}>(
          apiUrl(`/api/billing/orders?scope=${encodeURIComponent(mode)}&orderIds=${encodeURIComponent(orderId)}`),
        );
        if (!result.deleted) throw new Error('删除失败，请刷新后重试');
        selectedOrderIds.delete(orderId);
        cachedOrders = cachedOrders.filter(o => o.id !== orderId);
        updateOrderCounters(root, cachedOrders);
        renderOrdersTable(root, cachedOrders, mode, auth, mode === 'personal' || enterprisePurchase);
        refreshOrderSelectionUi(root, mode === 'personal' || enterprisePurchase);
      } catch (error) {
        delBtn.disabled = false;
        window.alert(error instanceof Error ? error.message : String(error));
      }
      return;
    }
  });

  // 异步获取数据并注入
  void hydrateBillingPage(root, auth, mode, enterprisePurchase);
  // 测试接口或网络较慢时，避免订单区长期停留在加载文案。
  window.setTimeout(() => {
    const tbody = root.querySelector<HTMLTableSectionElement>('#billingOrdersTbody');
    if (!tbody || !tbody.textContent?.includes('正在加载订单数据')) return;
    cachedOrders = [];
    updateOrderCounters(root, cachedOrders);
    renderOrdersTable(root, cachedOrders, mode, auth, mode === 'personal' || enterprisePurchase);
  }, 4500);
}

/**
 * 生成服务卡片网格 HTML。
 * 卡片集合来自后台目录，法务 Plus 作为基础版的层级内嵌在同一张卡片里。
 */
function servicesGridHtml(
  plans: ServicePlan[],
  plusPlan: ServicePlan | undefined,
  mode: 'personal' | 'enterprise',
  enterprisePurchase: boolean,
  entitlementModules: EntitlementModuleRecord[],
): string {
  if (!plans.length) return '<p class="billing-module-empty">暂无可售服务，请联系管理员上架。</p>';
  return plans.map(plan => renderModuleCardHtml(
    plan,
    mode,
    enterprisePurchase,
    entitlementModules.find(item => item.productId === plan.id) ?? null,
    sumEnterprisePurchasedSeats(cachedSubscriptions, plan.id),
    plan.categoryKey === 'legal' ? plusPlan : undefined,
    plan.categoryKey === 'legal' ? entitlementModules.find(item => item.productId === plusPlan?.id) ?? null : null,
  )).join('');
}

/** 后台目录发生变化后重绘服务卡片网格并重新绑定交互。 */
function refreshServicesGrid(
  root: HTMLElement,
  auth: AuthSnapshot,
  mode: 'personal' | 'enterprise',
  enterprisePurchase: boolean,
  entitlementModules: EntitlementModuleRecord[],
): void {
  const grid = root.querySelector<HTMLElement>('#billingModulesGrid');
  if (!grid) return;
  const plusPlan = cachedPlans.find(plan => plan.id === legalPlusPlan.id);
  const cards = cachedPlans.filter(plan => plan.id !== legalPlusPlan.id);
  grid.innerHTML = servicesGridHtml(cards, plusPlan, mode, enterprisePurchase, entitlementModules);
  bindServiceCardEvents(root, auth, mode, enterprisePurchase);
}

/** 绑定服务卡片上的订购、层级切换与席位详情交互。 */
function bindServiceCardEvents(
  root: HTMLElement,
  auth: AuthSnapshot,
  mode: 'personal' | 'enterprise',
  enterprisePurchase: boolean,
): void {
  // 模块订购按钮绑定 (唤起订购配置弹窗)
  root.querySelectorAll<HTMLButtonElement>('#billingModulesGrid [data-action-plan]').forEach(button => {
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const planId = button.dataset.actionPlan;
      const plan = cachedPlans.find(item => item.id === planId);
      if (!plan) return;
      openPurchaseConfigModal(root, auth, plan, mode, enterprisePurchase);
    });
  });

  // 法务合规助手基础版 / Plus 切换，不改变既有订购流程。
  root.querySelectorAll<HTMLButtonElement>('#billingModulesGrid [data-legal-tier]').forEach(button => {
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const tier = button.dataset.legalTier || 'base';
      const card = button.closest<HTMLElement>('.billing-module-card');
      if (!card) return;
      card.dataset.activeTier = tier;
      card.querySelectorAll<HTMLButtonElement>('[data-legal-tier]').forEach(item => {
        const active = item.dataset.legalTier === tier;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
      card.querySelectorAll<HTMLElement>('[data-legal-tier-panel]').forEach(panel => panel.classList.toggle('is-active', panel.dataset.legalTierPanel === tier));
    });
  });

  // 团队席位详情入口（点击卡片，管理员可查看分配详情）
  root.querySelectorAll<HTMLElement>('#billingModulesGrid [data-card-plan]').forEach(card => {
    card.addEventListener('click', (event) => {
      if (mode !== 'enterprise' || !enterprisePurchase) return;
      if ((event.target as HTMLElement).closest('button, input, select, a')) return;
      const planId = card.getAttribute('data-card-plan') || '';
      const entitlement = (cachedEntitlements?.enterprise?.modules ?? []).find(item => item.productId === planId);
      if (!entitlement?.available) return;
      const plan = cachedPlans.find(item => item.id === planId);
      if (!plan) return;
      void openEnterpriseSeatDetailModal(root, auth, plan);
    });
  });
}

/**
 * 生成单个专项服务卡片 HTML
 */
function renderModuleCardHtml(
  plan: ServicePlan,
  mode: 'personal' | 'enterprise',
  enterprisePurchase: boolean,
  entitlement: EntitlementModuleRecord | null,
  purchasedSeats: number,
  plusPlan?: ServicePlan,
  plusEntitlement: EntitlementModuleRecord | null = null,
): string {
  const isFinance = plan.categoryKey === 'finance';
  const unitText = mode === 'enterprise' ? '/月/人' : '/月';
  const priceDisplay = mode === 'enterprise' ? `¥${plan.price}` : `¥${plan.price}`;
  const isAvailable = Boolean(entitlement?.available);
  const badgeText = resolveModuleBadgeText(mode, entitlement);
  const clickableClass = mode === 'enterprise' && enterprisePurchase && isAvailable ? ' is-clickable' : '';
  const statText = renderModuleStatText(mode, entitlement, purchasedSeats);
  const validityText = renderModuleValidityHeaderText(entitlement);
  const defaultTier: 'base' | 'plus' = 'base';
  const renderTierBody = (tierPlan: ServicePlan, tierEntitlement: EntitlementModuleRecord | null, tier: 'base'|'plus') => {
    const available = Boolean(tierEntitlement?.available);
    // 价目在首屏渲染前已经从后台或本地有效缓存取得，后台刷新不会再显示占位文案。
    const displayPrice = `¥${tierPlan.price}`;
    const actionDisabled = mode === 'enterprise' && !enterprisePurchase;
    return `<div class="billing-tier-panel ${tier === defaultTier ? 'is-active' : ''}" data-legal-tier-panel="${tier}"><div class="billing-module-features">${tierPlan.features.map(f => `<span class="billing-module-feature"><svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>${escapeHtml(f)}</span>`).join('')}</div><div class="billing-module-bottom"><div class="billing-module-price-row"><div class="billing-module-price"><strong data-price-val="${tierPlan.id}">${displayPrice}</strong><span>${mode === 'enterprise' ? '/月/人' : '/月'}</span></div></div><div class="billing-module-actions">${mode === 'enterprise' && !enterprisePurchase ? '<button type="button" class="billing-btn" disabled>仅管理员可采购</button>' : `<button type="button" class="billing-btn ${available ? 'billing-btn-success' : 'billing-btn-primary'}" data-action-plan="${tierPlan.id}"${actionDisabled ? ' disabled' : ''}>${available ? '已开通 · 续订或加购' : '立即开通'}</button>`}</div></div></div>`;
  };
  const tierSwitch = plusPlan ? `<div class="billing-tier-switch" role="tablist" aria-label="法务合规助手方案"><button type="button" class="billing-tier-btn is-active" data-legal-tier="base" role="tab" aria-selected="true">基础版</button><button type="button" class="billing-tier-btn" data-legal-tier="plus" role="tab" aria-selected="false">Plus <span>增强能力</span></button></div>` : '';

  return `
  <article class="billing-module-card ${isFinance ? 'is-finance' : plan.categoryKey === 'legal' ? 'is-legal' : 'is-general'} ${(isAvailable || plusEntitlement?.available) ? 'is-active' : ''}${clickableClass}" data-card-plan="${plan.id}" data-active-tier="${defaultTier}">
    ${tierSwitch}
    <div class="billing-module-top">
      <div class="billing-module-title-box">
        <div class="billing-module-avatar">
          ${isFinance ? `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          ` : plan.categoryKey === 'legal' ? `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          ` : `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="22" height="22">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
          `}
        </div>
        <div>
          <h3 class="billing-module-name">${escapeHtml(plan.name)}${plan.highlight ? `<span class="billing-module-highlight">${escapeHtml(plan.highlight)}</span>` : ''}</h3>
        </div>
      </div>
      ${validityText ? `<span class="billing-module-validity" data-module-validity="${escapeHtml(plan.id)}" data-tier-indicator="base">${escapeHtml(validityText)}</span>` : ''}
      ${plusPlan && plusEntitlement?.available ? `<span class="billing-module-validity" data-module-validity="${escapeHtml(plusPlan.id)}" data-tier-indicator="plus">${escapeHtml(renderModuleValidityHeaderText(plusEntitlement))}</span>` : ''}
      ${badgeText ? `
      <span class="billing-badge ${isAvailable ? 'is-active' : 'is-inactive'}" data-module-badge="${plan.id}" data-tier-indicator="base">
        ${badgeText}
      </span>` : `<span class="billing-badge is-inactive" data-module-badge="${plan.id}" data-tier-indicator="base" hidden>未开通</span>`}
      ${plusPlan ? `<span class="billing-badge ${plusEntitlement?.available ? 'is-active' : 'is-inactive'}" data-module-badge="${escapeHtml(plusPlan.id)}" data-tier-indicator="plus" ${!resolveModuleBadgeText(mode, plusEntitlement) ? 'hidden' : ''}>${escapeHtml(resolveModuleBadgeText(mode, plusEntitlement))}</span>` : ''}
    </div>

    ${plusPlan ? `${renderTierBody(plan, entitlement, 'base')}${renderTierBody(plusPlan, plusEntitlement, 'plus')}` : renderTierBody(plan, entitlement, 'base')}
   <!-- tier content replaces the legacy single-plan feature/price block -->
    <div class="billing-module-features legacy-hidden" hidden>
      ${plan.features.map(f => `
        <span class="billing-module-feature">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          ${escapeHtml(f)}
        </span>
      `).join('')}
    </div>

    <div class="billing-module-bottom legacy-hidden" hidden>
      <div class="billing-module-price-row">
        <div class="billing-module-price">
          <strong data-price-val="${plan.id}">${priceDisplay}</strong>
          <span data-price-unit="${plan.id}">${unitText}</span>
        </div>
        ${statText ? `<span class="billing-module-stat" data-module-stat="${escapeHtml(plan.id)}">${escapeHtml(statText)}</span>` : ''}
      </div>

      <div class="billing-module-actions">
        ${mode === 'enterprise' && !enterprisePurchase ? `
          <button type="button" class="billing-btn" disabled title="仅团队管理员可采购席位">仅管理员可采购</button>
        ` : `
          <button type="button" class="billing-btn ${isAvailable ? 'billing-btn-success' : 'billing-btn-primary'}" data-action-plan="${plan.id}">
            ${isAvailable ? '已开通 · 续订或加购' : '立即开通'}
          </button>
        `}
      </div>
    </div>
  </article>
  `;
}

/**
 * 加载后端订阅与订单数据并更新界面
 */
async function hydrateBillingPage(root: HTMLElement, auth: AuthSnapshot, mode: 'personal' | 'enterprise', enterprisePurchase: boolean): Promise<void> {
  try {
    const withTimeout = <T,>(request: Promise<T>, fallback: T, timeoutMs = 8000): Promise<T> => Promise.race([
      request,
      new Promise<T>((resolve) => window.setTimeout(() => resolve(fallback), timeoutMs)),
    ]);
    const [{products, prices}, {subscriptions}, {orders}] = await Promise.all([
      withTimeout(api.get<BillingCatalogPayload>(apiUrl('/api/billing/products')), {products: [], prices: []}),
      withTimeout(api.get<{subscriptions: SubscriptionRecord[]}>(apiUrl(`/api/billing/subscriptions?scope=${mode}`)), {subscriptions: []}),
      withTimeout(api.get<{orders: OrderRecord[]}>(apiUrl(`/api/billing/orders?scope=${mode}`)), {orders: []}),
    ]);
    try {
      cachedEntitlements = await api.get<EntitlementsPayload>(apiUrl('/api/billing/entitlements'));
    } catch {
      /* 订单对账可能刚完成，授权快照刷新失败时沿用进入页面时的结果 */
    }

    const scopeEntitlements = mode === 'enterprise' ? cachedEntitlements?.enterprise?.modules : cachedEntitlements?.personal?.modules;
    const entitlementMap = new Map((scopeEntitlements ?? []).map(item => [item.productId, item]));
    const activeProductIds = new Set((scopeEntitlements ?? []).filter(item => item.available).map(item => item.productId));
    cachedSubscriptions = subscriptions;

    // 更新各服务计划的状态与价格。接口超时会返回空目录，此时保留首屏已经展示的有效价目，
    // 避免慢接口把真实价格回退成旧的硬编码默认值。
    const hasServerCatalog = products.length > 0 || prices.some(price => price.active && Number.isFinite(price.amount));
    const refreshedCatalog = hasServerCatalog
      ? mergeBillingCatalogPlans([...defaultPlans, legalPlusPlan], {products, prices})
      : cachedPlans;
    // 后台新增、停售或删除服务后，首屏卡片集合与最新目录不一致，需要整体重绘。
    const renderedCardIds = new Set([...root.querySelectorAll<HTMLElement>('#billingModulesGrid [data-card-plan]')].map(item => item.dataset.cardPlan || ''));
    cachedPlans = refreshedCatalog.map(plan => {
      const entitlement = entitlementMap.get(plan.id);
      const hasActiveSub = Boolean(entitlement?.available);

      return {
        ...plan,
        enabled: hasActiveSub || plan.enabled,
      };
    });
    if (hasServerCatalog) writeBillingCatalogCache(cachedPlans);
    const expectedCardIds = new Set(cachedPlans.filter(plan => plan.id !== legalPlusPlan.id).map(plan => plan.id));
    const catalogChanged = expectedCardIds.size !== renderedCardIds.size || [...expectedCardIds].some(id => !renderedCardIds.has(id));
    if (catalogChanged) refreshServicesGrid(root, auth, mode, enterprisePurchase, scopeEntitlements ?? []);
    // 更新模块卡片状态
    for (const plan of cachedPlans) {
      const card = root.querySelector<HTMLElement>(`[data-card-plan="${plan.id}"]`);
      if (!card && plan.id === legalPlusPlan.id) {
        const plusEntitlement = entitlementMap.get(plan.id);
        const legalCard = root.querySelector<HTMLElement>(`[data-card-plan="${defaultPlans.find(item => item.categoryKey === 'legal')?.id}"]`);
        if (legalCard) {
          updateModuleBadgeElement(legalCard, plan.id, mode, plusEntitlement ?? null);
          updateModuleValidityElement(legalCard, plan.id, plusEntitlement ?? null);
          legalCard.classList.toggle('is-active', Boolean(plusEntitlement?.available || entitlementMap.get(legalCard.dataset.cardPlan || '')?.available));
        }
        const plusPrice = root.querySelector<HTMLElement>(`[data-price-val="${plan.id}"]`);
        if (plusPrice) plusPrice.textContent = `¥${plan.price}`;
        const plusBtn = root.querySelector<HTMLButtonElement>(`[data-action-plan="${plan.id}"]`);
        if (plusBtn && !(mode === 'enterprise' && !enterprisePurchase)) {
          plusBtn.disabled = false;
          const active = Boolean(plusEntitlement?.available);
          plusBtn.className = `billing-btn ${active ? 'billing-btn-success' : 'billing-btn-primary'}`;
          plusBtn.textContent = active ? '已开通 · 续订或加购' : '立即开通';
        }
        return;
      }
      if (!card) continue;

      const entitlement = entitlementMap.get(plan.id);
      const hasActiveSub = Boolean(entitlement?.available);
      card.classList.toggle('is-active', hasActiveSub || (plan.categoryKey === 'legal' && Boolean(entitlementMap.get(LEGAL_PLUS_PRODUCT_ID)?.available)));

      updateModuleBadgeElement(card, plan.id, mode, entitlement ?? null);
      updateModuleValidityElement(card, plan.id, entitlement ?? null);

      const priceVal = card.querySelector<HTMLElement>(`[data-price-val="${plan.id}"]`);
      if (priceVal) priceVal.textContent = `¥${plan.price}`;

      const actionBtn = card.querySelector<HTMLButtonElement>(`[data-action-plan="${plan.id}"]`);
      if (actionBtn && !(mode === 'enterprise' && !enterprisePurchase)) {
        actionBtn.disabled = false;
        actionBtn.className = `billing-btn ${hasActiveSub ? 'billing-btn-success' : 'billing-btn-primary'}`;
        actionBtn.textContent = hasActiveSub ? '已开通 · 续订或加购' : '立即开通';
      }

      const purchasedSeats = sumEnterprisePurchasedSeats(subscriptions, plan.id);
      const statText = renderModuleStatText(mode, entitlement ?? null, purchasedSeats);
      updateModuleStatElement(card, plan.id, statText);

      card.classList.toggle('is-clickable', mode === 'enterprise' && enterprisePurchase && hasActiveSub);
    }

    // 缓存订单数据并更新计数与表格
    cachedOrders = orders;
    selectedOrderIds = new Set([...selectedOrderIds].filter(id => orders.some(order => order.id === id)));
    updateOrderCounters(root, cachedOrders);
    renderOrdersTable(root, cachedOrders, mode, auth, mode === 'personal' || enterprisePurchase);
    refreshOrderSelectionUi(root, mode === 'personal' || enterprisePurchase);
  } catch (err) {
    console.error('[billing.hydrate]', err);
    const tbody = root.querySelector<HTMLTableSectionElement>('#billingOrdersTbody');
    if (tbody) {
      tbody.innerHTML = `<tr><td colspan="8" class="billing-empty-state"><p>数据加载暂不可用，请稍后刷新重试。</p></td></tr>`;
    }
  }
}

/**
 * 更新订单筛选计数器。
 */
function updateOrderCounters(root: HTMLElement, orders: OrderRecord[]): void {
  const fulfilledCount = orders.filter(o => o.status === 'fulfilled' || o.status === 'paid').length;
  const pendingCount = orders.filter(o => o.status === 'pending').length;
  const cancelledCount = orders.filter(o => o.status === 'cancelled').length;

  const countAll = root.querySelector<HTMLElement>('#countAll');
  const countFulfilled = root.querySelector<HTMLElement>('#countFulfilled');
  const countPending = root.querySelector<HTMLElement>('#countPending');
  const countCancelled = root.querySelector<HTMLElement>('#countCancelled');
  const summaryEl = root.querySelector<HTMLElement>('#billingOrdersSummary');

  if (countAll) countAll.textContent = String(orders.length);
  if (countFulfilled) countFulfilled.textContent = String(fulfilledCount);
  if (countPending) countPending.textContent = String(pendingCount);
  if (countCancelled) countCancelled.textContent = String(cancelledCount);
  if (summaryEl) summaryEl.textContent = `共 ${orders.length} 笔订单`;
}

/** 刷新订单多选工具栏状态。 */
function refreshOrderSelectionUi(root: HTMLElement, canDeleteOrders: boolean): void {
  const bulkDelete = root.querySelector<HTMLButtonElement>('#billingBulkDeleteOrders');
  const selectHead = root.querySelector<HTMLElement>('#billingOrderSelectHead');
  const selectAll = root.querySelector<HTMLInputElement>('#billingSelectAllOrders');
  const visibleIds = [...root.querySelectorAll<HTMLInputElement>('.billing-order-select')].map(input => input.value);
  const selectedVisible = visibleIds.filter(id => selectedOrderIds.has(id));
  if (bulkDelete) {
    bulkDelete.hidden = !canDeleteOrders;
    bulkDelete.disabled = selectedVisible.length === 0;
    bulkDelete.textContent = selectedVisible.length > 0 ? `删除所选（${selectedVisible.length}）` : '删除所选';
  }
  if (selectHead) selectHead.hidden = !canDeleteOrders;
  if (selectAll) {
    selectAll.disabled = visibleIds.length === 0;
    selectAll.checked = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
    selectAll.indeterminate = selectedVisible.length > 0 && selectedVisible.length < visibleIds.length;
  }
}

/** 批量删除当前用户选中的订单记录。 */
async function deleteSelectedOrders(root: HTMLElement, mode: 'personal' | 'enterprise', canDeleteOrders: boolean): Promise<void> {
  if (!canDeleteOrders) return;
  const orderIds = [...selectedOrderIds].filter(id => cachedOrders.some(order => order.id === id));
  if (!orderIds.length) return;
  if (!window.confirm(`确定删除选中的 ${orderIds.length} 条订单记录吗？支付、授权和审计数据将保留，仅不再显示在列表中。`)) return;
  const bulkDelete = root.querySelector<HTMLButtonElement>('#billingBulkDeleteOrders');
  try {
    if (bulkDelete) bulkDelete.disabled = true;
    const result = await api.delete<{ok: boolean; deleted: number}>(
      apiUrl(`/api/billing/orders?scope=${encodeURIComponent(mode)}`),
      {orderIds, scope: mode},
    );
    if (!result.deleted) throw new Error('删除失败，请刷新后重试');
    orderIds.forEach(id => selectedOrderIds.delete(id));
    cachedOrders = cachedOrders.filter(order => !orderIds.includes(order.id));
    updateOrderCounters(root, cachedOrders);
    renderOrdersTable(root, cachedOrders, mode, activeAuthSnapshot!, canDeleteOrders);
    refreshOrderSelectionUi(root, canDeleteOrders);
  } catch (error) {
    window.alert(error instanceof Error ? error.message : String(error));
    refreshOrderSelectionUi(root, canDeleteOrders);
  }
}

/**
 * 渲染订单数据表格
 */
function renderOrdersTable(
  root: HTMLElement,
  orders: OrderRecord[],
  mode: 'personal' | 'enterprise',
  auth: AuthSnapshot,
  canDeleteOrders: boolean,
): void {
  const tbody = root.querySelector<HTMLTableSectionElement>('#billingOrdersTbody');
  if (!tbody) return;
  activeAuthSnapshot = auth;
  const columnCount = canDeleteOrders ? 8 : 7;

  const filtered = orders.filter(order => {
    if (currentFilter === 'all') return true;
    if (currentFilter === 'fulfilled') return order.status === 'fulfilled' || order.status === 'paid';
    return order.status === currentFilter;
  });

  if (filtered.length === 0) {
    tbody.innerHTML = `
      <tr>
        <td colspan="${columnCount}" class="billing-empty-state">
          <div class="billing-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="24" height="24">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </div>
          <h3>${currentFilter === 'all' ? '暂无任何订单记录' : '当前筛选分类下无订单'}</h3>
          <p>${currentFilter === 'all' ? '开通专项服务或选购席位后，订单记录将在此实时展示。' : '可切换其它状态查看全部历史账单。'}</p>
        </td>
      </tr>
    `;
    return;
  }

  tbody.innerHTML = filtered.map(order => {
    const isPaid = order.status === 'fulfilled' || order.status === 'paid';
    const isPending = order.status === 'pending';
    const isCancelled = order.status === 'cancelled';

    const statusBadge = isPaid
      ? `<span class="billing-badge is-active">● 已支付</span>`
      : isPending
      ? `<span class="billing-badge is-pending">● 待支付</span>`
      : isCancelled
      ? `<span class="billing-badge is-cancelled">● 已取消</span>`
      : `<span class="billing-badge is-inactive">${escapeHtml(order.status)}</span>`;

    const productName = order.productSnapshot?.name || '功能模块订阅';
    const productId = order.productSnapshot?.id || '';
    const versionText = productId === LEGAL_PLUS_PRODUCT_ID || /\bplus\b/i.test(productName)
      ? 'Plus'
      : '基础版';
    const seatText = order.ownerType === 'enterprise'
      ? String(Number(order.seatQuantity || 1))
      : '1';

    const timeDisplay = isPaid && order.paidAt
      ? formatDateTime(order.paidAt)
      : formatDateTime(order.createdAt);

    return `
      <tr data-order-row="${escapeHtml(order.id)}">
        ${canDeleteOrders ? `
        <td class="billing-order-select-col">
          <input type="checkbox" class="billing-order-select" value="${escapeHtml(order.id)}" aria-label="选择订单 ${escapeHtml(order.orderNo)}" ${selectedOrderIds.has(order.id) ? 'checked' : ''}>
        </td>` : ''}
        <td>
          <span class="billing-order-time">${escapeHtml(timeDisplay)}</span>
        </td>
        <td>
          <strong>${escapeHtml(productName)}</strong>
        </td>
        <td>
          <span class="billing-order-version${versionText === 'Plus' ? ' is-plus' : ''}">${escapeHtml(versionText)}</span>
        </td>
        <td>
          <span class="billing-order-seat">${escapeHtml(seatText)}</span>
        </td>
        <td>
          <span class="billing-order-amount">${formatCurrency(order.amount)}</span>
        </td>
        <td>
          ${statusBadge}
        </td>
        <td style="text-align:right">
          <div class="billing-order-actions" style="justify-content:flex-end">
            <button type="button" class="billing-action-link" data-copy-orderno="${escapeHtml(order.orderNo)}" title="复制订单号 ${escapeHtml(order.orderNo)}">复制单号</button>
            ${isPending ? `
              <button type="button" class="billing-action-pay" data-order-pay="${escapeHtml(order.id)}">
                立即支付
              </button>
            ` : ''}
            ${canDeleteOrders ? `
              <button type="button" class="billing-action-del" data-order-delete="${escapeHtml(order.id)}" title="删除该条记录">
                删除记录
              </button>
            ` : ''}
          </div>
        </td>
      </tr>
    `;
  }).join('');
  refreshOrderSelectionUi(root, canDeleteOrders);
}

/**
 * 弹出团队席位详情对话框，展示已购/已分配及成员有效期。
 */
async function openEnterpriseSeatDetailModal(root: HTMLElement, auth: AuthSnapshot, plan: ServicePlan): Promise<void> {
  root.querySelector('.billing-modal-overlay')?.remove();

  const modal = document.createElement('div');
  modal.className = 'billing-modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="billing-dialog billing-seat-detail-dialog">
      <button type="button" class="billing-dialog-close" aria-label="关闭对话框">×</button>
      <div class="billing-dialog-header">
        <span class="billing-dialog-tag">团队席位详情</span>
        <h2 class="billing-dialog-title">${escapeHtml(plan.name)} · 团队席位</h2>
        <p class="billing-dialog-desc">正在加载席位分配与成员授权…</p>
      </div>
      <div class="billing-seat-detail-body">
        <p class="billing-seat-detail-loading">加载中…</p>
      </div>
    </div>
  `;

  const close = (): void => modal.remove();
  modal.querySelector('.billing-dialog-close')?.addEventListener('click', close);
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
  root.appendChild(modal);

  const body = modal.querySelector<HTMLElement>('.billing-seat-detail-body');
  const desc = modal.querySelector<HTMLElement>('.billing-dialog-desc');
  if (!body) return;

  try {
    const [{members}, {seats}, {subscriptions}] = await Promise.all([
      api.get<{members: OrganizationMemberRecord[]}>(apiUrl(`/api/organizations/${encodeURIComponent(auth.organization.id)}/members`)),
      api.get<{seats: ModuleSeatRecord[]}>(apiUrl(`/api/organizations/${encodeURIComponent(auth.organization.id)}/module-seats`)),
      api.get<{subscriptions: SubscriptionRecord[]}>(apiUrl('/api/billing/subscriptions?scope=enterprise')),
    ]);

    const memberNames = new Map(members.map(item => [item.id, item.displayName || item.email]));
    const purchasedSeats = sumEnterprisePurchasedSeats(subscriptions, plan.id);
    const productSeats = seats.filter(item => item.productId === plan.id);
    const activeSeats = productSeats.filter(item => item.status === 'active' && !isSeatExpired(item));
    const expiredSeats = productSeats.filter(item => isSeatExpired(item));

    if (desc) {
      desc.textContent = purchasedSeats > 0
        ? `已购 ${purchasedSeats} 席，已授权 ${activeSeats.length} 名成员`
        : '当前为平台代开通授权，可在企业管理页继续分配席位';
    }

    const seatRows = productSeats.length > 0
      ? productSeats.map(seat => {
        const expired = isSeatExpired(seat);
        const memberLabel = escapeHtml(memberNames.get(seat.userId) || seat.userId);
        const expiryLabel = seat.endsAt ? formatDateOnly(seat.endsAt) : '永久有效';
        return `
          <tr class="${expired ? 'is-expired' : ''}">
            <td>
              <strong>${memberLabel}</strong>
              ${expired ? '<span class="billing-seat-expired-tag">已到期</span>' : ''}
            </td>
            <td>${escapeHtml(expiryLabel)}</td>
            <td style="text-align:right">
              ${expired ? `<button type="button" class="billing-action-del" data-revoke-seat="${escapeHtml(seat.id)}">删除记录</button>` : '—'}
            </td>
          </tr>
        `;
      }).join('')
      : '<tr><td colspan="3" class="billing-empty-state"><p>暂无成员席位分配记录</p></td></tr>';

    body.innerHTML = `
      <div class="billing-seat-summary">
        <div><span>已购席位</span><strong>${purchasedSeats > 0 ? `${purchasedSeats} 席` : '平台代开通'}</strong></div>
        <div><span>已授权成员</span><strong>${activeSeats.length} 人</strong></div>
        <div><span>到期待清理</span><strong class="${expiredSeats.length > 0 ? 'is-warning' : ''}">${expiredSeats.length} 条</strong></div>
      </div>
      <div class="billing-table-responsive">
        <table class="billing-table billing-seat-table">
          <thead>
            <tr>
              <th>成员</th>
              <th>有效期至</th>
              <th style="text-align:right">操作</th>
            </tr>
          </thead>
          <tbody>${seatRows}</tbody>
        </table>
      </div>
    `;

    body.querySelectorAll<HTMLButtonElement>('[data-revoke-seat]').forEach(button => {
      button.addEventListener('click', async () => {
        const seatId = button.dataset.revokeSeat || '';
        if (!window.confirm('确定删除该条已到期的席位授权记录吗？')) return;
        try {
          button.disabled = true;
          await api.delete(apiUrl(`/api/organizations/${encodeURIComponent(auth.organization.id)}/module-seats/${encodeURIComponent(seatId)}`));
          close();
          void renderBillingPage(root, auth);
        } catch (error) {
          button.disabled = false;
          window.alert(error instanceof Error ? error.message : String(error));
        }
      });
    });
  } catch (error) {
    body.innerHTML = `<p class="billing-seat-detail-error">${escapeHtml(error instanceof Error ? error.message : String(error))}</p>`;
    if (desc) desc.textContent = '席位详情加载失败，请稍后重试';
  }
}

/**
 * 弹出订购与席位配置对话框 (Google Material 规范)
 */
function openPurchaseConfigModal(
  root: HTMLElement,
  auth: AuthSnapshot,
  plan: ServicePlan,
  mode: 'personal' | 'enterprise',
  enterprisePurchase: boolean,
): void {
  root.querySelector('.billing-modal-overlay')?.remove();

  const isEnterprise = mode === 'enterprise';
  let seatQuantity = isEnterprise ? 5 : 1;
  const unitPrice = plan.price;

  const modal = document.createElement('div');
  modal.className = 'billing-modal-overlay billing-purchase-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  const updateCalculatedPrice = (): void => {
    const total = (unitPrice * seatQuantity).toFixed(2);
    const totalEl = modal.querySelector<HTMLElement>('#configTotalPrice');
    const submitBtn = modal.querySelector<HTMLButtonElement>('#configSubmitBtn');
    if (totalEl) totalEl.textContent = formatCurrency(Number(total));
    if (submitBtn) submitBtn.textContent = '确认并扫码支付';
  };

  modal.innerHTML = `
    <div class="billing-dialog">
      <button type="button" class="billing-dialog-close" aria-label="关闭对话框">×</button>
      <div class="billing-dialog-header">
        <span class="billing-dialog-tag">${isEnterprise ? '团队席位采购' : '个人专属开通'}</span>
        <h2 class="billing-dialog-title">订购${escapeHtml(plan.name)}</h2>
        <p class="billing-dialog-desc">
          ${isEnterprise
            ? `以团队主体「${escapeHtml(auth.organization.name)}」采购席位授权`
            : `以个人账号「${escapeHtml(auth.user.displayName || auth.user.email)}」开通专属授权`
          }
        </p>
      </div>

      ${isEnterprise ? `
        <div class="billing-form-group">
          <label class="billing-form-label">
            <span>席位数量</span>
            <span class="billing-stepper">
              <button type="button" class="billing-stepper-btn" id="stepperMinus" aria-label="减少席位">−</button>
              <input type="number" class="billing-stepper-input" id="stepperInput" min="5" max="10000" value="5" aria-label="席位数量">
              <button type="button" class="billing-stepper-btn" id="stepperPlus" aria-label="增加席位">＋</button>
            </span>
          </label>
          <div class="billing-seat-chips">
            <button type="button" class="billing-chip is-active" data-chip-seat="5">5</button>
            <button type="button" class="billing-chip" data-chip-seat="10">10</button>
            <button type="button" class="billing-chip" data-chip-seat="20">20</button>
            <button type="button" class="billing-chip" data-chip-seat="50">50</button>
          </div>
        </div>
        <label class="billing-cycle-row">
          <span>计费周期</span>
          <select aria-label="计费周期">
            <option value="month">按月</option>
          </select>
        </label>
      ` : ''}

      <div class="billing-summary-box">
        <div class="billing-summary-row">
          <span>单价</span>
          <strong>¥${plan.price} ${isEnterprise ? '/ 人 / 月' : '/ 月'}</strong>
        </div>
        <div class="billing-summary-row is-total">
          <span>合计金额</span>
          <strong id="configTotalPrice">${formatCurrency(unitPrice * seatQuantity)}</strong>
        </div>
      </div>

      <div class="billing-dialog-actions">
        <button type="button" class="billing-btn" id="configCancelBtn">取消</button>
        <button type="button" class="billing-btn billing-btn-primary" id="configSubmitBtn">
          确认并微信扫码支付
        </button>
      </div>
    </div>
  `;

  root.append(modal);

  // 步进控制与快速点选绑定
  if (isEnterprise) {
    const input = modal.querySelector<HTMLInputElement>('#stepperInput');
    const minus = modal.querySelector<HTMLButtonElement>('#stepperMinus');
    const plus = modal.querySelector<HTMLButtonElement>('#stepperPlus');
    const chips = modal.querySelectorAll<HTMLButtonElement>('[data-chip-seat]');

    const setQuantity = (val: number): void => {
      seatQuantity = Math.max(5, Math.min(10000, Math.floor(val) || 5));
      if (input) input.value = String(seatQuantity);
      chips.forEach(chip => {
        chip.classList.toggle('is-active', Number(chip.dataset.chipSeat) === seatQuantity);
      });
      updateCalculatedPrice();
    };

    minus?.addEventListener('click', () => setQuantity(seatQuantity - 1));
    plus?.addEventListener('click', () => setQuantity(seatQuantity + 1));
    input?.addEventListener('input', () => setQuantity(Number(input.value)));
    chips.forEach(chip => {
      chip.addEventListener('click', () => setQuantity(Number(chip.dataset.chipSeat)));
    });
  }

  // 关闭对话框处理
  const closeModal = (): void => modal.remove();
  modal.querySelector('.billing-dialog-close')?.addEventListener('click', closeModal);
  modal.querySelector('#configCancelBtn')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  // 提交下单并打开微信二维码
  const submitBtn = modal.querySelector<HTMLButtonElement>('#configSubmitBtn');
  submitBtn?.addEventListener('click', async () => {
    try {
      submitBtn.disabled = true;
      submitBtn.textContent = '正在创建订单…';

    const idempotencyKey = crypto.randomUUID();
      const created = await api.post<{order: {id: string}}>(apiUrl('/api/billing/orders'), {
        priceId: plan.priceId,
        idempotencyKey,
        ownerType: isEnterprise ? 'enterprise' : 'personal',
        seatQuantity,
      });

      const payment = await api.post<{codeUrl: string}>(
        apiUrl(`/api/billing/orders/${encodeURIComponent(created.order.id)}/wechat/native-payment`),
      );

      closeModal();
      showPaymentModal(
        root,
        plan,
        auth.user.displayName || auth.user.email,
        created.order.id,
        payment.codeUrl,
        seatQuantity,
        isEnterprise,
        unitPrice * seatQuantity,
        mode,
      );
  } catch (error) {
    window.alert(error instanceof Error ? error.message : String(error));
      submitBtn.disabled = false;
      updateCalculatedPrice();
    }
  });

  updateCalculatedPrice();
}

/**
 * 弹出微信 Native 扫码支付对话框 (Google Material 规范)
 */
function showPaymentModal(
  root: HTMLElement,
  plan: ServicePlan,
  paymentAccount: string,
  orderId: string,
  codeUrl: string,
  seatQuantity = 1,
  enterprisePurchase = false,
  totalAmount?: number,
  billingMode: 'personal' | 'enterprise' = 'personal',
): void {
  root.querySelector('.billing-modal-overlay')?.remove();

  const finalAmount = totalAmount ?? plan.price * seatQuantity;
  const modal = document.createElement('div');
  modal.className = 'billing-modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  modal.innerHTML = `
    <div class="billing-dialog" style="text-align:center">
      <button type="button" class="billing-dialog-close" aria-label="关闭支付窗口">×</button>

      <div style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;background:#ecfdf5;color:#047857;font-size:12px;font-weight:600;margin-bottom:12px">
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M8.5 2C4.36 2 1 4.91 1 8.5c0 1.99 1.03 3.78 2.65 4.98L3 17.5l4.31-1.44c.38.07.78.11 1.19.11.23 0 .46-.02.68-.05A7.37 7.37 0 0 1 9 14.5c0-3.59 3.36-6.5 7.5-6.5.4 0 .79.03 1.17.08C17.07 4.7 13.16 2 8.5 2z"/>
          <path d="M16.5 9.5c-3.59 0-6.5 2.46-6.5 5.5 0 1.7.9 3.22 2.31 4.24l-.56 2.76 2.92-1.22c.57.14 1.18.22 1.83.22 3.59 0 6.5-2.46 6.5-5.5s-2.91-5.5-6.5-5.5z"/>
        </svg>
        微信 Native 安全支付
      </div>

      <h2 style="margin:0 0 4px;font-size:20px;color:#0f172a">${escapeHtml(plan.name)}</h2>
      <p style="margin:0;color:#64748b;font-size:13px">
        ${enterprisePurchase ? `团队席位 ${seatQuantity} 人` : '个人账号专属授权'} · 账号 ${escapeHtml(paymentAccount)}
      </p>

      <div style="margin:16px 0 8px">
        <span style="font-size:13px;color:#64748b">应付金额</span>
        <div style="font-size:32px;font-weight:700;color:#0284c7;line-height:1.2;margin-top:2px">
          ¥${finalAmount.toFixed(2)}
        </div>
      </div>

      <div class="billing-qr-box">
        <img alt="微信扫码支付二维码" data-payment-qr style="display:none">
        <div data-payment-loading style="padding:40px 0;color:#64748b;font-size:13px">正在生成微信支付二维码…</div>
        <div class="billing-qr-status" data-payment-status style="display:none">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          请使用微信扫一扫完成付款
        </div>
      </div>

      <p style="margin:0;color:#94a3b8;font-size:12px;line-height:1.5">
        支付成功后，授权将实时自动下发并更新，无需手动刷新。
      </p>
    </div>
  `;

  root.append(modal);

  const qrImg = modal.querySelector<HTMLImageElement>('[data-payment-qr]');
  const qrLoading = modal.querySelector<HTMLElement>('[data-payment-loading]');
  const qrStatus = modal.querySelector<HTMLElement>('[data-payment-status]');

  // 渲染二维码
  void QRCode.toDataURL(codeUrl, {
    width: 220,
    margin: 1,
    color: {dark: '#0f172a', light: '#ffffff'},
  }).then(url => {
    if (qrImg && qrLoading && qrStatus) {
      qrImg.src = url;
      qrImg.style.display = 'block';
      qrLoading.style.display = 'none';
      qrStatus.style.display = 'inline-flex';
    }
  }).catch(() => {
    if (qrLoading) qrLoading.textContent = '二维码生成失败，请关闭后重新尝试。';
  });

  // 轮询支付状态
  let timer = 0;
  let finished = false;

  const checkPayment = async (): Promise<void> => {
    if (finished) return;
    try {
      const result = await api.get<{orders: Array<{id: string; status: string}>}>(apiUrl(`/api/billing/orders?scope=${billingMode}`));
      const order = result.orders.find(item => item.id === orderId);

      if (order?.status === 'fulfilled' || order?.status === 'paid') {
        finished = true;
        window.clearInterval(timer);
        if (qrStatus) {
          qrStatus.innerHTML = `
            <span style="color:#059669;font-weight:700">✓ 支付成功！授权已实时生效</span>
          `;
        }
        window.setTimeout(() => {
          modal.remove();
          // 重新刷新整页数据
          if (activeAuthSnapshot) {
            void renderBillingPage(root, activeAuthSnapshot);
          } else {
            window.location.reload();
          }
        }, 1200);
      }
    } catch {
      /* 网络轻微波动保留二维码继续轮询 */
    }
  };

  timer = window.setInterval(() => void checkPayment(), 2500);
  void checkPayment();

  // 关闭处理
  const closeModal = (): void => {
    window.clearInterval(timer);
    modal.remove();
  };

  modal.querySelector('.billing-dialog-close')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });
}
